import { useCallback, useEffect, useRef, useState } from 'react';

export const FORM_FIELD_DEBOUNCE_MS = 300;

const toDisplayString = (value) =>
  value === undefined || value === null ? '' : String(value);

/**
 * Keeps responsive local input state; commits to Formik/parent on a debounce timer.
 * Call flush() on blur so the latest value is saved immediately.
 */
export const useDebouncedInput = (
  syncValue,
  onCommit,
  delay = FORM_FIELD_DEBOUNCE_MS
) => {
  const [localValue, setLocalValue] = useState(() =>
    toDisplayString(syncValue)
  );
  const timerRef = useRef(null);
  const onCommitRef = useRef(onCommit);
  const localRef = useRef(localValue);

  onCommitRef.current = onCommit;
  localRef.current = localValue;

  useEffect(() => {
    setLocalValue(toDisplayString(syncValue));
  }, [syncValue]);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onCommitRef.current(localRef.current);
  }, []);

  const setValue = useCallback(
    (next) => {
      const display = toDisplayString(next);
      setLocalValue(display);
      localRef.current = display;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        onCommitRef.current(display);
      }, delay);
    },
    [delay]
  );

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    []
  );

  return { localValue, setValue, flush };
};
